import {
  NUMBER_OF_DAYS_BACK,
  customerIdentifierSearch,
  fetcher,
} from "@repo/api";
import { useSettings } from "@repo/ui";
import { PROJECT_REFRESH_INTERVAL_MS } from "@repo/utils";
import { isDefined } from "@repo/utils";
import uniq from "lodash/uniq";
import uniqBy from "lodash/uniqBy";
import useSWR from "swr";

export interface SearchOrderResult {
  businessUnitCode?: string;
  businessUnitType?: string;
  clientSystem?: "BOKA" | "ISELL" | "SALJA";
  countryCode?: string;
  currencyCode?: string;
  familyNumber?: string;
  globalCustomerId?: string;
  orderAmount?: number;
  orderCategory?: string;
  orderCreationDate: string;
  orderCreationMethod?: string;
  orderNo: string;
  orderNoSource?: string;
  orderPaid?: boolean;
  orderStatus: string;
  orderStatusCode?: string;
  orderType: string;
}

export const isOrderNumber = (searchTerm?: string) =>
  /\d{9,}/.test(searchTerm ?? "");

const fetchOrdersByPartyId = async (url: string) => {
  const result = await fetcher.saljaSearch.get<{
    orders: SearchOrderResult[];
  }>(url);

  return result?.orders;
};

export const useSearchCustomerOrders = (
  isValidatingCustomer: boolean,
  customerType?: Customer["customerType"],
  email?: string,
  originalPartyId?: string
) => {
  // Search with either email or partyId
  const shouldSearch =
    isValidatingCustomer === false &&
    (Boolean(email) || Boolean(originalPartyId)) &&
    Boolean(customerType);
  const { market } = useSettings();

  const { data, isValidating } = useSWR(
    shouldSearch ? `customer-orders/${email}/${originalPartyId}` : null,
    async () => {
      const partyIds = await customerIdentifierSearch(
        market,
        customerType,
        email
      );
      const allPartyIds = uniq([...partyIds, originalPartyId]);

      const promises = allPartyIds.map((id) =>
        fetchOrdersByPartyId(`/orders/customer/${NUMBER_OF_DAYS_BACK}/${id}`)
      );

      const res = await Promise.allSettled(promises).then((results) =>
        results
          .flatMap((r) => {
            if (r.status === "fulfilled") {
              return r?.value;
            }
            return undefined;
          })
          .filter(isDefined)
      );
      return uniqBy(res, "orderNo");
    },
    {
      keepPreviousData: true,
      revalidateOnFocus: false,
      revalidateIfStale: false,
      dedupingInterval: PROJECT_REFRESH_INTERVAL_MS,
      shouldRetryOnError: false,
    }
  );

  return {
    data,
    isValidating,
  };
};
